/**
 * Cookie Consent State Store
 *
 * Manages GDPR-compliant cookie consent preferences using Zustand with localStorage persistence.
 * Controls which types of cookies/tracking the user has consented to.
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export interface CookieConsent {
  necessary: boolean // Always true - essential for site function
  analytics: boolean // PostHog tracking
  marketing: boolean // Future use - advertising/remarketing
}

interface CookieConsentState {
  hasConsented: boolean
  consent: CookieConsent
  consentTimestamp: string | null

  // Actions
  acceptAll: () => void
  rejectAll: () => void
  updateConsent: (consent: Partial<CookieConsent>) => void
  resetConsent: () => void
}

const DEFAULT_CONSENT: CookieConsent = {
  necessary: true,
  analytics: false,
  marketing: false,
}

export const useCookieConsentStore = create<CookieConsentState>()(
  persist(
    (set) => ({
      hasConsented: false,
      consent: DEFAULT_CONSENT,
      consentTimestamp: null,

      acceptAll: () =>
        set({
          hasConsented: true,
          consent: {
            necessary: true,
            analytics: true,
            marketing: true,
          },
          consentTimestamp: new Date().toISOString(),
        }),

      rejectAll: () =>
        set({
          hasConsented: true,
          consent: {
            necessary: true,
            analytics: false,
            marketing: false,
          },
          consentTimestamp: new Date().toISOString(),
        }),

      updateConsent: (newConsent) =>
        set((state) => ({
          hasConsented: true,
          consent: {
            ...state.consent,
            ...newConsent,
            necessary: true, // Always force necessary to true
          },
          consentTimestamp: new Date().toISOString(),
        })),

      resetConsent: () =>
        set({
          hasConsented: false,
          consent: DEFAULT_CONSENT,
          consentTimestamp: null,
        }),
    }),
    {
      name: 'tonesmith-cookie-consent',
    }
  )
)
